import { Heart } from 'lucide-react';
import { Card, CardContent } from '~/components/ui/card';
import { StepHeader } from '~/components/forms/StepHeader';
import { SignatureField } from '~/components/forms/SignatureField';
import { StudentMedicalChunk } from '~/components/form-chunks/StudentMedicalChunk';
import { useEnrollingStudents } from '~/hooks/useEnrollingStudents';

/**
 * StudentMedicalStep - Per-student medical information forms.
 * Delegates to StudentMedicalChunk for the actual form content.
 */
export function StudentMedicalStep({ formData, updateFormData, onReviewComplete }: any) {
  const allStudents = useEnrollingStudents(formData);
  const medicalInfo = formData?.studentMedicalInfo || {};

  const handleUpdate = (updatedMedicalInfo: Record<string, any>) => {
    updateFormData('studentMedicalInfo', updatedMedicalInfo);
  };

  if (allStudents.length === 0) {
    return (
      <div className="space-y-6">
        <StepHeader icon={Heart} title="Medical Information" />
        <Card className="max-w-xl mx-auto">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No students selected for enrollment. Please go back and select at least one student.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StepHeader
        icon={Heart}
        title="Medical Information"
        description="Please provide or confirm medical information for each student."
      />

      <div className="max-w-2xl mx-auto">
        <StudentMedicalChunk
          students={allStudents}
          medicalInfo={medicalInfo}
          onUpdate={handleUpdate}
          onReviewComplete={onReviewComplete}
          showOtcMedications={true}
        />

        {/* Medical Consent Signature */}
        <SignatureField
          value={formData?.medicalConsentSignature || ''}
          onChange={(value) => updateFormData('medicalConsentSignature', value)}
          label="Medical Consent Signature"
          description="By signing below, you authorize Immanuel Classical to administer the medications you have approved above to your child(ren) in case of illness."
          consentText="I hereby give permission for the adult staff or teachers of Immanuel Classical to administer the over-the-counter medications I have selected above to my child(ren) when needed. I understand that I can update these preferences at any time by contacting the school office."
          id="medical-consent-signature"
          required
        />
      </div>
    </div>
  );
}

export default StudentMedicalStep;
